import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CableRoundedIcon from "@mui/icons-material/CableRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  addPlatformWorkspaceAIOperationTopup,
  approvePlatformWorkspace,
  disablePlatformConnector,
  enablePlatformConnector,
  getPlatformOverview,
  getPlatformWorkspaceAIAllowance,
  listPlatformConnectors,
  listPlatformPlans,
  listPlatformStores,
  listPlatformWorkspaces,
  listPlatformWorkspaceSubscriptions,
  permanentlyDeletePlatformWorkspace,
  reactivatePlatformWorkspace,
  renewPlatformWorkspace,
  rejectPlatformWorkspace,
  revokePlatformConnector,
  suspendPlatformWorkspace,
  updatePlatformWorkspaceAIAllowance,
} from "../api/platform";
import { getApiError } from "../api/errors";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

const PAGE_SIZE = 25;

function statusColor(value) {
  switch (value) {
    case "active":
    case "online":
    case "completed":
    case "paid":
      return "success";
    case "pending":
    case "pending_approval":
    case "trial":
    case "running":
      return "info";
    case "disabled":
    case "offline":
    case "partial":
    case "suspended":
    case "access_expired":
    case "overdue":
    case "warning":
    case "critical":
      return "warning";
    case "revoked":
    case "failed":
    case "rejected":
    case "exhausted":
      return "error";
    default:
      return "default";
  }
}

function MetricCard({ label, value, detail, icon, tone = "blue" }) {
  const tones = {
    blue: { background: "#eff6ff", foreground: "#1d4ed8" },
    green: { background: "#ecfdf5", foreground: "#047857" },
    amber: { background: "#fffbeb", foreground: "#b45309" },
    red: { background: "#fef2f2", foreground: "#b91c1c" },
  };
  const color = tones[tone] || tones.blue;

  return (
    <Card sx={{ height: "100%", borderRadius: 4, border: "1px solid #e2e8f0" }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={800}>
              {label}
            </Typography>
            <Typography variant="h4" fontWeight={950} sx={{ mt: 0.6 }}>
              {Number(value || 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {detail}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              bgcolor: color.background,
              color: color.foreground,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function WorkspaceActions({ workspace, canControl, canDelete, onAction, onHistory, onAllowance, onDelete }) {
  const historyButton = (
    <Tooltip title="Subscription history">
      <IconButton size="small" onClick={() => onHistory(workspace)}>
        <ReceiptLongRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const allowanceButton = workspace.effective_status === "active" || workspace.effective_status === "suspended" ? (
    <Tooltip title="AI usage allowance">
      <IconButton size="small" onClick={() => onAllowance(workspace)}>
        <AutoAwesomeRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;


  const deleteButton = canDelete ? (
    <Tooltip title="Permanently delete workspace">
      <IconButton size="small" color="error" onClick={() => onDelete(workspace)}>
        <DeleteForeverRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  if (!canControl) {
    return (
      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
        <Chip size="small" label="Read only" variant="outlined" />
        {allowanceButton}
        {historyButton}
        {deleteButton}
      </Stack>
    );
  }

  if (workspace.effective_status === "pending_approval") {
    return (
      <Stack direction="row" spacing={0.7} justifyContent="flex-end" alignItems="center">
        <Button size="small" variant="contained" onClick={() => onAction("approve", workspace)}>
          Approve
        </Button>
        <Button size="small" color="error" variant="outlined" onClick={() => onAction("reject", workspace)}>
          Reject
        </Button>
        {historyButton}
        {deleteButton}
      </Stack>
    );
  }

  if (workspace.effective_status === "active") {
    return (
      <Stack direction="row" spacing={0.7} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
        <Button size="small" variant="contained" onClick={() => onAction("renew", workspace)}>
          Renew 30 days
        </Button>
        <Button size="small" color="warning" variant="outlined" onClick={() => onAction("suspend", workspace)}>
          Suspend
        </Button>
        {allowanceButton}
        {historyButton}
        {deleteButton}
      </Stack>
    );
  }

  if (workspace.effective_status === "access_expired") {
    return (
      <Stack direction="row" spacing={0.7} justifyContent="flex-end" alignItems="center">
        <Button size="small" variant="contained" onClick={() => onAction("renew", workspace)}>
          Record payment
        </Button>
        {historyButton}
        {deleteButton}
      </Stack>
    );
  }

  if (workspace.effective_status === "suspended") {
    return (
      <Stack direction="row" spacing={0.7} justifyContent="flex-end" alignItems="center">
        <Button size="small" variant="contained" onClick={() => onAction("reactivate", workspace)}>
          Review & reactivate
        </Button>
        {allowanceButton}
        {historyButton}
        {deleteButton}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
      <Chip size="small" label="No action" variant="outlined" />
      {allowanceButton}
      {historyButton}
        {deleteButton}
    </Stack>
  );
}

export function WorkspaceActionDialog({ action, onClose, onSubmit, submitting }) {
  const [reason, setReason] = useState("");
  const [billingStatus, setBillingStatus] = useState("paid");
  const [planCode, setPlanCode] = useState("starter");
  const [paymentReference, setPaymentReference] = useState("");
  const [operationLimit, setOperationLimit] = useState("");
  const [voiceLimitMinutes, setVoiceLimitMinutes] = useState("");
  const [costBudget, setCostBudget] = useState("");
  const [warningPercent, setWarningPercent] = useState("70");
  const [criticalPercent, setCriticalPercent] = useState("90");
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState("");

  useEffect(() => {
    const inheritsAllowance = action?.type === "renew" || action?.type === "reactivate";
    setReason("");
    setBillingStatus(action?.workspace?.billing_status === "trial" ? "trial" : "paid");
    setPlanCode("");
    setPaymentReference("");
    setOperationLimit("");
    setVoiceLimitMinutes("");
    setCostBudget("");
    setWarningPercent(inheritsAllowance ? "" : "70");
    setCriticalPercent(inheritsAllowance ? "" : "90");
    setPlans([]);
    setPlansError("");
    if (action) {
      listPlatformPlans()
        .then((items) => {
          const available = Array.isArray(items) ? items : [];
          setPlans(available);
          const currentCode = action.workspace?.plan_code;
          setPlanCode(
            available.some((plan) => plan.code === currentCode)
              ? currentCode
              : (available[0]?.code || ""),
          );
        })
        .catch((requestError) => {
          setPlansError(getApiError(requestError, "Could not load commercial plans."));
        });
    }
  }, [action]);

  if (!action) return null;

  const isRenewal = action.type === "renew";
  const inheritsAllowance = isRenewal || action.type === "reactivate";
  const needsAccessTerms = ["approve", "reactivate"].includes(action.type);
  const paymentRequired = isRenewal || (needsAccessTerms && billingStatus === "paid");
  const parsedOperationLimit = operationLimit === "" ? null : Number(operationLimit);
  const parsedVoiceLimitMinutes = voiceLimitMinutes === "" ? null : Number(voiceLimitMinutes);
  const parsedCostBudget = costBudget === "" ? null : Number(costBudget);
  const parsedWarning = warningPercent === "" ? null : Number(warningPercent);
  const parsedCritical = criticalPercent === "" ? null : Number(criticalPercent);
  const allowanceValid =
    (parsedOperationLimit === null || (Number.isInteger(parsedOperationLimit) && parsedOperationLimit >= 1))
    && (parsedVoiceLimitMinutes === null || (Number.isFinite(parsedVoiceLimitMinutes) && parsedVoiceLimitMinutes > 0))
    && (parsedCostBudget === null || (Number.isFinite(parsedCostBudget) && parsedCostBudget > 0))
    && (parsedWarning === null || (Number.isInteger(parsedWarning) && parsedWarning >= 1 && parsedWarning <= 99))
    && (parsedCritical === null || (Number.isInteger(parsedCritical) && parsedCritical >= 2 && parsedCritical <= 100))
    && (parsedWarning === null || parsedCritical === null || parsedWarning < parsedCritical);
  const selectedCommercialPlan = plans.find((plan) => plan.code === planCode) || null;
  const valid =
    reason.trim().length >= 3
    && ((!needsAccessTerms && !isRenewal) || planCode.trim().length >= 2)
    && (!paymentRequired || paymentReference.trim().length >= 2)
    && allowanceValid;

  const handleSubmit = () => {
    const payload = { reason: reason.trim() };
    if (isRenewal) {
      payload.payment_reference = paymentReference.trim();
      payload.plan_code = planCode.trim() || null;
    } else if (needsAccessTerms) {
      payload.billing_status = billingStatus;
      payload.plan_code = planCode.trim();
      payload.payment_reference = paymentReference.trim() || null;
    }
    if (needsAccessTerms || isRenewal) {
      if (parsedOperationLimit !== null) payload.ai_operation_limit = parsedOperationLimit;
      if (parsedVoiceLimitMinutes !== null) payload.voice_duration_limit_seconds = Math.round(parsedVoiceLimitMinutes * 60);
      if (parsedCostBudget !== null) payload.ai_cost_budget_usd = parsedCostBudget;
      if (parsedWarning !== null) payload.ai_warning_percent = parsedWarning;
      if (parsedCritical !== null) payload.ai_critical_percent = parsedCritical;
    }
    onSubmit(action, payload);
  };

  const actionDescription = action.type === "approve"
    ? "Approval starts a fixed 30-day access period and unlocks tenant business endpoints."
    : action.type === "suspend"
      ? "Suspension immediately blocks tenant features and connector synchronization."
      : action.type === "renew"
        ? "Recording this payment starts the selected plan immediately for a new 30-day period. The current period closes now."
        : action.type === "reactivate"
          ? "Reactivation starts a new fixed 30-day access period."
          : "Rejection keeps login available but blocks all business features.";

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle fontWeight={900}>
        {action.type === "renew" ? "Renew subscription" : `${titleCase(action.type)} workspace`}
      </DialogTitle>
      <DialogContent>
        <Alert
          severity={action.type === "reject" ? "error" : "warning"}
          sx={{ mb: 2, borderRadius: 3 }}
        >
          {actionDescription}
        </Alert>

        <Typography fontWeight={900}>{action.workspace.workspace_name}</Typography>
        <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ mb: 2 }}>
          {action.workspace.workspace_id}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Current plan: <strong>{titleCase(action.workspace.plan_code || "Unknown")}</strong>
        </Typography>

        {(needsAccessTerms || isRenewal) && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            {needsAccessTerms && (
              <FormControl fullWidth>
                <InputLabel id="billing-status-label">Access / billing status</InputLabel>
                <Select
                  labelId="billing-status-label"
                  label="Access / billing status"
                  value={billingStatus}
                  onChange={(event) => setBillingStatus(event.target.value)}
                >
                  <MenuItem value="paid">Paid manually</MenuItem>
                  <MenuItem value="trial">Trial access</MenuItem>
                  <MenuItem value="waived">Complimentary / waived</MenuItem>
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel id="plan-code-label">Plan</InputLabel>
              <Select
                labelId="plan-code-label"
                label="Plan"
                value={planCode}
                onChange={(event) => setPlanCode(event.target.value)}
                disabled={!plans.length}
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.code} value={plan.code}>{plan.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {plansError && <Alert severity="error">{plansError}</Alert>}
            {selectedCommercialPlan && (
              <Card variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                <Typography fontWeight={900}>{selectedCommercialPlan.name}</Typography>
                <Typography variant="h6" fontWeight={900}>${Number(selectedCommercialPlan.monthly_price_usd).toLocaleString()}/month</Typography>
                <Typography variant="body2">{Number(selectedCommercialPlan.ai_operations).toLocaleString()} AI operations</Typography>
                <Typography variant="body2">{selectedCommercialPlan.voice_minutes} voice minutes</Typography>
                <Typography variant="body2">Basic Admin AI: {selectedCommercialPlan.ai_admin_enabled ? "Enabled" : "Disabled"}</Typography>
                <Typography variant="body2">Admin AI Agent: {selectedCommercialPlan.ai_agent_enabled ? "Enabled" : "Disabled"}</Typography>
              </Card>
            )}
            <TextField
              fullWidth
              required={paymentRequired}
              label="Payment reference"
              value={paymentReference}
              onChange={(event) => setPaymentReference(event.target.value)}
              helperText={paymentRequired ? "Required and protected against duplicate renewal." : "Optional for trial or waived access."}
              inputProps={{ maxLength: 150 }}
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Typography fontWeight={900}>Optional overrides</Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave AI and voice allowances blank to use the selected plan defaults.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="AI operation allowance"
                  value={operationLimit}
                  onChange={(event) => setOperationLimit(event.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  helperText="Optional. Blank uses the selected plan default."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Voice allowance (minutes)"
                  value={voiceLimitMinutes}
                  onChange={(event) => setVoiceLimitMinutes(event.target.value)}
                  inputProps={{ min: 0.1, step: 0.1 }}
                  helperText="Optional. Blank uses the selected plan default."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Internal AI cost budget (USD)"
                  value={costBudget}
                  onChange={(event) => setCostBudget(event.target.value)}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  helperText={inheritsAllowance ? "Leave blank to inherit the previous budget." : "Visible only to platform operators."}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Warning threshold (%)"
                  value={warningPercent}
                  onChange={(event) => setWarningPercent(event.target.value)}
                  inputProps={{ min: 1, max: 99, step: 1 }}
                  helperText={inheritsAllowance ? "Blank inherits the previous threshold." : "Recommended: 70"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Critical threshold (%)"
                  value={criticalPercent}
                  onChange={(event) => setCriticalPercent(event.target.value)}
                  inputProps={{ min: 2, max: 100, step: 1 }}
                  helperText={inheritsAllowance ? "Blank inherits the previous threshold." : "Recommended: 90"}
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Subscription duration is fixed by the platform at 30 days. The expiration date cannot be edited manually.
            </Alert>
          </Stack>
        )}

        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          label="Platform decision reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText="Stored permanently in the workspace audit log."
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color={action.type === "reject" ? "error" : "primary"}
          disabled={!valid || submitting}
          onClick={handleSubmit}
        >
          {submitting ? <CircularProgress size={21} color="inherit" /> : action.type === "renew" ? "Record payment & renew" : titleCase(action.type)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function WorkspaceDeleteDialog({ workspace, onClose, onSubmit, submitting }) {
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    setReason("");
    setConfirmation("");
  }, [workspace]);

  if (!workspace) return null;

  const expected = `DELETE ${workspace.workspace_id}`;
  const valid = reason.trim().length >= 3 && confirmation.trim() === expected;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle fontWeight={950} color="error.main">
        Permanently delete workspace
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>
          This permanently removes this tenant's database data, persisted WhatsApp session credentials,
          connector records, and workspace files. Users are deleted only when they do not belong to
          another workspace and do not hold platform access. This action cannot be undone.
        </Alert>
        <Typography fontWeight={900}>{workspace.workspace_name}</Typography>
        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
          {workspace.workspace_id}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Deletion reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          inputProps={{ maxLength: 500 }}
          sx={{ mt: 2 }}
        />
        <Typography variant="body2" sx={{ mt: 2, mb: 0.75 }}>
          Type <Box component="span" fontFamily="monospace" fontWeight={900}>{expected}</Box> to confirm.
        </Typography>
        <TextField
          fullWidth
          label="Confirmation"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="off"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!valid || submitting}
          onClick={() => onSubmit(workspace, {
            reason: reason.trim(),
            confirmation: confirmation.trim(),
          })}
        >
          {submitting ? "Deleting…" : "Delete permanently"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubscriptionHistoryDialog({ workspace, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [periods, setPeriods] = useState([]);

  useEffect(() => {
    let active = true;
    async function loadHistory() {
      setLoading(true);
      setError("");
      try {
        const response = await listPlatformWorkspaceSubscriptions(workspace.workspace_id, {
          page: 1,
          page_size: 100,
        });
        if (active) setPeriods(response.items || []);
      } catch (requestError) {
        if (active) setError(getApiError(requestError, "Could not load subscription history."));
      } finally {
        if (active) setLoading(false);
      }
    }
    loadHistory();
    return () => {
      active = false;
    };
  }, [workspace.workspace_id]);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle fontWeight={900}>Subscription history · {workspace.workspace_name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingPanel label="Loading subscription history…" />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : periods.length ? (
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>AI allowance</TableCell>
                  <TableCell>Recorded by</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.map((period) => (
                  <TableRow key={period.subscription_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={800}>{formatDate(period.period_start)}</Typography>
                      <Typography variant="caption" color="text.secondary">to {formatDate(period.period_end)} · {period.duration_days} days</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={titleCase(period.billing_status)} color={statusColor(period.billing_status)} variant="outlined" />
                      {period.superseded_at && (
                        <Chip size="small" label="Superseded" color="default" variant="outlined" sx={{ ml: 0.5 }} />
                      )}
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{period.payment_reference || "No payment reference"}</Typography>
                    </TableCell>
                    <TableCell>{period.plan_code}</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <Typography variant="body2" fontWeight={800}>
                        {period.ai_operation_limit
                          ? `${Number(period.ai_operation_limit).toLocaleString()} operations`
                          : "Unlimited operations"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {period.ai_cost_budget_usd
                          ? `$${Number(period.ai_cost_budget_usd).toFixed(2)} internal budget`
                          : "No internal cost budget"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {period.ai_warning_percent}% / {period.ai_critical_percent}% thresholds
                      </Typography>
                    </TableCell>
                    <TableCell>{period.recorded_by_email || "System / migration"}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{period.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyPanel icon="🧾" title="No subscription periods" description="A period appears after approval or renewal." />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function AIAllowanceDialog({ workspace, canControl, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [topUpSaving, setTopUpSaving] = useState(false);
  const [error, setError] = useState("");
  const [topUpMessage, setTopUpMessage] = useState("");
  const [allowance, setAllowance] = useState(null);
  const [operationLimit, setOperationLimit] = useState("");
  const [voiceLimitMinutes, setVoiceLimitMinutes] = useState("");
  const [costBudget, setCostBudget] = useState("");
  const [warningPercent, setWarningPercent] = useState("70");
  const [criticalPercent, setCriticalPercent] = useState("90");
  const [reason, setReason] = useState("");
  const [topUpOperations, setTopUpOperations] = useState("");
  const [topUpPaymentReference, setTopUpPaymentReference] = useState("");
  const [topUpReason, setTopUpReason] = useState("");

  useEffect(() => {
    let active = true;
    async function loadAllowance() {
      setLoading(true);
      setError("");
      setTopUpMessage("");
      try {
        const response = await getPlatformWorkspaceAIAllowance(workspace.workspace_id);
        if (!active) return;
        setAllowance(response);
        setOperationLimit(response.base_operation_limit ?? response.operation_limit ?? "");
        setVoiceLimitMinutes(response.voice_limit_minutes ?? "");
        setCostBudget(response.cost_budget_usd ?? "");
        setWarningPercent(String(response.warning_percent));
        setCriticalPercent(String(response.critical_percent));
      } catch (requestError) {
        if (active) setError(getApiError(requestError, "Could not load the AI allowance."));
      } finally {
        if (active) setLoading(false);
      }
    }
    loadAllowance();
    return () => {
      active = false;
    };
  }, [workspace.workspace_id]);

  const parsedOperationLimit = operationLimit === "" ? null : Number(operationLimit);
  const parsedVoiceLimitMinutes = voiceLimitMinutes === "" ? null : Number(voiceLimitMinutes);
  const parsedCostBudget = costBudget === "" ? null : Number(costBudget);
  const parsedWarning = Number(warningPercent);
  const parsedCritical = Number(criticalPercent);
  const valid =
    allowance
    && reason.trim().length >= 3
    && (parsedOperationLimit === null || (Number.isInteger(parsedOperationLimit) && parsedOperationLimit >= 1))
    && (parsedVoiceLimitMinutes === null || (Number.isFinite(parsedVoiceLimitMinutes) && parsedVoiceLimitMinutes > 0))
    && (parsedCostBudget === null || (Number.isFinite(parsedCostBudget) && parsedCostBudget > 0))
    && Number.isInteger(parsedWarning)
    && parsedWarning >= 1
    && parsedWarning <= 99
    && Number.isInteger(parsedCritical)
    && parsedCritical >= 2
    && parsedCritical <= 100
    && parsedWarning < parsedCritical;

  const parsedTopUpOperations = Number(topUpOperations);
  const topUpValid =
    allowance
    && allowance.base_operation_limit !== null
    && Number.isInteger(parsedTopUpOperations)
    && parsedTopUpOperations >= 1
    && topUpPaymentReference.trim().length >= 2
    && topUpReason.trim().length >= 3;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await updatePlatformWorkspaceAIAllowance(workspace.workspace_id, {
        ai_operation_limit: parsedOperationLimit,
        voice_duration_limit_seconds: parsedVoiceLimitMinutes === null ? null : Math.round(parsedVoiceLimitMinutes * 60),
        ai_cost_budget_usd: parsedCostBudget,
        ai_warning_percent: parsedWarning,
        ai_critical_percent: parsedCritical,
        expected_version: allowance.version,
        reason: reason.trim(),
      });
      setAllowance(updated);
      onSaved(updated);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not update the AI allowance."));
    } finally {
      setSaving(false);
    }
  };

  const recordTopUp = async () => {
    setTopUpSaving(true);
    setError("");
    setTopUpMessage("");
    try {
      const result = await addPlatformWorkspaceAIOperationTopup(workspace.workspace_id, {
        operations: parsedTopUpOperations,
        payment_reference: topUpPaymentReference.trim(),
        reason: topUpReason.trim(),
      });
      setAllowance(result.allowance);
      setOperationLimit(result.allowance.base_operation_limit ?? "");
      setTopUpOperations("");
      setTopUpPaymentReference("");
      setTopUpReason("");
      setTopUpMessage(
        result.already_recorded
          ? "This payment reference was already recorded. No duplicate operations were added."
          : `${Number(result.topup.operations).toLocaleString()} AI operations were added to the current subscription period.`,
      );
    } catch (requestError) {
      setError(getApiError(requestError, "Could not record the AI operation top-up."));
    } finally {
      setTopUpSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle fontWeight={900}>AI usage allowance · {workspace.workspace_name}</DialogTitle>
      <DialogContent>
        {loading ? (
          <LoadingPanel label="Loading AI allowance…" />
        ) : error && !allowance ? (
          <Alert severity="error">{error}</Alert>
        ) : allowance ? (
          <Stack spacing={2.2}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              One AI operation equals one user request that reaches the AI. Multiple planner and response model calls inside that request still count as one operation. Paid operation top-ups add capacity to the current subscription period without resetting usage or extending the subscription.
            </Alert>
            {Number(allowance.unpriced_call_count || 0) > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                {Number(allowance.unpriced_call_count).toLocaleString()} model call(s) have no active price record, so the internal estimated cost is incomplete.
              </Alert>
            )}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Used operations</Typography>
                    <Typography variant="h5" fontWeight={950}>{Number(allowance.used_operations).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Number(allowance.model_call_count || 0).toLocaleString()} model calls
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Remaining</Typography>
                    <Typography variant="h5" fontWeight={950}>
                      {allowance.remaining_operations === null ? "Unlimited" : Number(allowance.remaining_operations).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {allowance.operation_limit === null
                        ? "Unlimited operation allowance"
                        : `${Number(allowance.operation_limit).toLocaleString()} effective limit`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Voice used</Typography>
                    <Typography variant="h5" fontWeight={950}>{Number(allowance.used_voice_minutes || 0).toLocaleString()} min</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {allowance.voice_limit_minutes === null ? "Unlimited" : `${Number(allowance.voice_limit_minutes).toLocaleString()} min included`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Voice remaining</Typography>
                    <Typography variant="h5" fontWeight={950}>
                      {allowance.remaining_voice_minutes === null ? "Unlimited" : `${Number(allowance.remaining_voice_minutes).toLocaleString()} min`}
                    </Typography>
                    <Chip size="small" sx={{ mt: 0.8 }} label={titleCase(allowance.voice_severity)} color={statusColor(allowance.voice_severity)} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Estimated AI cost</Typography>
                    <Typography variant="h5" fontWeight={950}>${Number(allowance.estimated_cost_usd || 0).toFixed(4)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>Status</Typography>
                    <Chip size="small" sx={{ mt: 0.8 }} label={titleCase(allowance.severity)} color={statusColor(allowance.severity)} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card variant="outlined" sx={{ borderRadius: 3, bgcolor: "#f8fafc" }}>
              <CardContent sx={{ py: 1.8 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Box>
                    <Typography fontWeight={900}>AI operations this period</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {allowance.base_operation_limit === null
                        ? "Unlimited base allowance"
                        : `${Number(allowance.base_operation_limit).toLocaleString()} base + ${Number(allowance.top_up_operations || 0).toLocaleString()} top-up = ${Number(allowance.operation_limit).toLocaleString()} effective operations`}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${Number(allowance.top_up_count || 0).toLocaleString()} top-up${Number(allowance.top_up_count || 0) === 1 ? "" : "s"}`}
                  />
                </Stack>
              </CardContent>
            </Card>

            <Typography variant="body2" color="text.secondary">
              Period: {formatDate(allowance.period_start)} to {formatDate(allowance.period_end)} · Plan {allowance.plan_code} · Version {allowance.version}
            </Typography>

            {canControl && (
              <Card variant="outlined" sx={{ borderRadius: 3, borderColor: "#bfdbfe", bgcolor: "#f8fbff" }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography fontWeight={950}>Record AI operation top-up</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add paid operations to this subscription period only. Existing usage stays unchanged, the subscription end date stays unchanged, and unused top-up operations do not roll into the next period.
                      </Typography>
                    </Box>
                    {topUpMessage && <Alert severity="success">{topUpMessage}</Alert>}
                    {allowance.base_operation_limit === null ? (
                      <Alert severity="info">
                        This subscription already has unlimited AI operations, so an operation top-up is not applicable.
                      </Alert>
                    ) : (
                      <>
                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Operations to add"
                              value={topUpOperations}
                              onChange={(event) => setTopUpOperations(event.target.value)}
                              inputProps={{ min: 1, step: 1 }}
                              placeholder="e.g. 500"
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              label="Payment reference"
                              value={topUpPaymentReference}
                              onChange={(event) => setTopUpPaymentReference(event.target.value)}
                              inputProps={{ maxLength: 150 }}
                              helperText="Required for safe retry/idempotency. Reusing the same reference never grants the top-up twice."
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              label="Top-up reason / note"
                              value={topUpReason}
                              onChange={(event) => setTopUpReason(event.target.value)}
                              inputProps={{ maxLength: 500 }}
                              helperText={`Stored in the audit log. This top-up expires with the current period on ${formatDate(allowance.period_end)}.`}
                            />
                          </Grid>
                        </Grid>
                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            disabled={!topUpValid || topUpSaving || saving}
                            onClick={recordTopUp}
                          >
                            {topUpSaving ? <CircularProgress size={21} color="inherit" /> : "Record top-up"}
                          </Button>
                        </Stack>
                      </>
                    )}

                    {allowance.recent_topups?.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={900}>
                          CURRENT PERIOD TOP-UPS
                        </Typography>
                        <Stack spacing={0.8} mt={0.8}>
                          {allowance.recent_topups.map((topup) => (
                            <Box
                              key={topup.topup_id}
                              sx={{
                                px: 1.4,
                                py: 1.1,
                                border: "1px solid #e2e8f0",
                                borderRadius: 2,
                                bgcolor: "#fff",
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={0.8}
                                justifyContent="space-between"
                              >
                                <Typography variant="body2" fontWeight={850}>
                                  +{Number(topup.operations).toLocaleString()} operations · {topup.payment_reference}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(topup.created_at)}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {topup.recorded_by_email || "Platform operator"} · {topup.reason}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={!canControl}
                  type="number"
                  label="Base AI operation allowance"
                  value={operationLimit}
                  onChange={(event) => setOperationLimit(event.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  helperText="Plan/base allowance for this subscription period. Top-ups are tracked separately. Blank means unlimited operations."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={!canControl}
                  type="number"
                  label="Voice allowance (minutes)"
                  value={voiceLimitMinutes}
                  onChange={(event) => setVoiceLimitMinutes(event.target.value)}
                  inputProps={{ min: 0.1, step: 0.1 }}
                  helperText="Blank means unlimited voice usage."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={!canControl}
                  type="number"
                  label="Internal AI cost budget (USD)"
                  value={costBudget}
                  onChange={(event) => setCostBudget(event.target.value)}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  helperText="Internal profitability monitor; never shown to the tenant."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={!canControl}
                  type="number"
                  label="Warning threshold (%)"
                  value={warningPercent}
                  onChange={(event) => setWarningPercent(event.target.value)}
                  inputProps={{ min: 1, max: 99, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  disabled={!canControl}
                  type="number"
                  label="Critical threshold (%)"
                  value={criticalPercent}
                  onChange={(event) => setCriticalPercent(event.target.value)}
                  inputProps={{ min: 2, max: 100, step: 1 }}
                />
              </Grid>
            </Grid>
            {canControl && (
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                label="Base allowance change reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                inputProps={{ maxLength: 500 }}
                helperText="Only needed when changing the base allowance/settings. Stored permanently in the workspace audit log."
              />
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving || topUpSaving}>Close</Button>
        {canControl && allowance && (
          <Button variant="contained" disabled={!valid || saving || topUpSaving} onClick={save}>
            {saving ? <CircularProgress size={21} color="inherit" /> : "Save base allowance"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ConnectorActionDialog({ action, onClose, onSubmit, submitting }) {
  const [reason, setReason] = useState("");
  const [confirmValue, setConfirmValue] = useState("");

  useEffect(() => {
    setReason("");
    setConfirmValue("");
  }, [action]);

  if (!action) return null;
  const revokeValid =
    action.type !== "revoke"
    || confirmValue.trim() === action.connector.connector_id;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle fontWeight={900}>{titleCase(action.type)} connector</DialogTitle>
      <DialogContent>
        <Alert severity={action.type === "revoke" ? "error" : "warning"} sx={{ mb: 2, borderRadius: 3 }}>
          {action.type === "revoke"
            ? "Revocation is permanent. A new connector must be created and activated."
            : `This changes synchronization for ${action.connector.store_name}.`}
        </Alert>
        <Typography fontWeight={850}>{action.connector.name}</Typography>
        <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ mb: 2 }}>
          {action.connector.connector_id}
        </Typography>
        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          label="Operational reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          inputProps={{ maxLength: 500 }}
        />
        {action.type === "revoke" && (
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label="Type the connector ID to confirm"
            value={confirmValue}
            onChange={(event) => setConfirmValue(event.target.value)}
            placeholder={action.connector.connector_id}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color={action.type === "revoke" ? "error" : "primary"}
          onClick={() => onSubmit(action, reason.trim())}
          disabled={submitting || reason.trim().length < 3 || !revokeValid}
        >
          {submitting ? <CircularProgress size={21} color="inherit" /> : titleCase(action.type)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PlatformOperations() {
  const { platformRole } = useAuth();
  const canControl = ["super_admin", "operations"].includes(platformRole);
  const canDelete = platformRole === "super_admin";
  const [tab, setTab] = useState(0);
  const [overview, setOverview] = useState(null);
  const [workspaces, setWorkspaces] = useState({ items: [], total: 0 });
  const [connectors, setConnectors] = useState({ items: [], total: 0 });
  const [stores, setStores] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  const [workspaceStatus, setWorkspaceStatus] = useState("");
  const [billingStatus, setBillingStatus] = useState("");
  const [onboardingMethod, setOnboardingMethod] = useState("");
  const [connectorStatus, setConnectorStatus] = useState("");
  const [health, setHealth] = useState("");
  const [storeStatus, setStoreStatus] = useState("");
  const [workspacePage, setWorkspacePage] = useState(1);
  const [connectorPage, setConnectorPage] = useState(1);
  const [storePage, setStorePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [workspaceAction, setWorkspaceAction] = useState(null);
  const [workspaceDelete, setWorkspaceDelete] = useState(null);
  const [subscriptionHistoryWorkspace, setSubscriptionHistoryWorkspace] = useState(null);
  const [allowanceWorkspace, setAllowanceWorkspace] = useState(null);
  const [connectorAction, setConnectorAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadOverview = useCallback(async () => {
    setOverview(await getPlatformOverview());
  }, []);

  const loadWorkspaces = useCallback(async () => {
    setWorkspaces(await listPlatformWorkspaces({
      page: workspacePage,
      page_size: PAGE_SIZE,
      search: debouncedSearch || undefined,
      workspace_status: workspaceStatus || undefined,
      billing_status: billingStatus || undefined,
      onboarding_method: onboardingMethod || undefined,
    }));
  }, [billingStatus, debouncedSearch, onboardingMethod, workspacePage, workspaceStatus]);

  const loadConnectors = useCallback(async () => {
    setConnectors(await listPlatformConnectors({
      page: connectorPage,
      page_size: PAGE_SIZE,
      search: debouncedSearch || undefined,
      connector_status: connectorStatus || undefined,
      health: health || undefined,
    }));
  }, [connectorPage, connectorStatus, debouncedSearch, health]);

  const loadStores = useCallback(async () => {
    setStores(await listPlatformStores({
      page: storePage,
      page_size: PAGE_SIZE,
      search: debouncedSearch || undefined,
      store_status: storeStatus || undefined,
    }));
  }, [debouncedSearch, storePage, storeStatus]);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const selectedLoader = tab === 0
        ? loadWorkspaces
        : tab === 1
          ? loadConnectors
          : loadStores;
      await Promise.all([loadOverview(), selectedLoader()]);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load platform operations."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadConnectors, loadOverview, loadStores, loadWorkspaces, tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => load({ silent: true }), 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    setWorkspacePage(1);
    setConnectorPage(1);
    setStorePage(1);
  }, [debouncedSearch, workspaceStatus, billingStatus, onboardingMethod, connectorStatus, health, storeStatus, tab]);

  const pageCount = useMemo(() => {
    const total = tab === 0
      ? workspaces.total
      : tab === 1
        ? connectors.total
        : stores.total;
    return Math.max(1, Math.ceil(Number(total || 0) / PAGE_SIZE));
  }, [connectors.total, stores.total, tab, workspaces.total]);

  const submitWorkspaceAction = async (action, payload) => {
    setSubmitting(true);
    setError("");
    try {
      const id = action.workspace.workspace_id;
      if (action.type === "approve") await approvePlatformWorkspace(id, payload);
      else if (action.type === "reject") await rejectPlatformWorkspace(id, payload.reason);
      else if (action.type === "suspend") await suspendPlatformWorkspace(id, payload.reason);
      else if (action.type === "renew") await renewPlatformWorkspace(id, payload);
      else await reactivatePlatformWorkspace(id, payload);
      setToast(`Workspace ${action.type} completed.`);
      setWorkspaceAction(null);
      await load({ silent: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Workspace action failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitWorkspaceDelete = async (workspace, payload) => {
    setSubmitting(true);
    setError("");
    try {
      const result = await permanentlyDeletePlatformWorkspace(workspace.workspace_id, payload);
      setToast(result.storage_cleanup_complete
        ? "Workspace permanently deleted."
        : "Workspace deleted, but storage cleanup needs attention.");
      setWorkspaceDelete(null);
      await load({ silent: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Permanent workspace deletion failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitConnectorAction = async (action, reason) => {
    setSubmitting(true);
    setError("");
    try {
      const id = action.connector.connector_id;
      if (action.type === "disable") await disablePlatformConnector(id, reason);
      else if (action.type === "enable") await enablePlatformConnector(id, reason);
      else await revokePlatformConnector(id, reason);
      setToast(`Connector ${action.type} completed.`);
      setConnectorAction(null);
      await load({ silent: true });
    } catch (requestError) {
      setError(getApiError(requestError, "Connector action failed."));
    } finally {
      setSubmitting(false);
    }
  };

  const currentPage = tab === 0 ? workspacePage : tab === 1 ? connectorPage : storePage;
  const setCurrentPage = tab === 0 ? setWorkspacePage : tab === 1 ? setConnectorPage : setStorePage;

  return (
    <MainLayout>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={2} mb={3}>
        <Box>
          <Stack direction="row" spacing={1.3} alignItems="center">
            <AdminPanelSettingsRoundedIcon color="primary" />
            <Typography variant="h4" fontWeight={950} letterSpacing={-0.8}>
              Platform operations
            </Typography>
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Internal approval, manual billing, tenant access, store, and connector control plane.
          </Typography>
        </Box>
        <Tooltip title="Refresh platform status">
          <span>
            <IconButton onClick={() => load({ silent: true })} disabled={refreshing} sx={{ bgcolor: "white", border: "1px solid #e2e8f0" }}>
              {refreshing ? <CircularProgress size={20} /> : <RefreshRoundedIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>{error}</Alert>}

      <Alert severity={canControl ? "warning" : "info"} sx={{ mb: 3, borderRadius: 3 }}>
        {canControl
          ? "New registrations remain locked until a platform operator approves access. Every approval, rejection, suspension, reactivation, and connector action requires a reason and is written to the workspace audit trail."
          : "Your platform role is read-only. Approval, billing, workspace, and connector controls are restricted to super administrators and operations staff."}
      </Alert>

      <Grid container spacing={2} mb={3.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Pending applications" value={overview?.pending_workspaces} detail={`${overview?.total_workspaces || 0} total workspaces`} icon={<BusinessRoundedIcon />} tone="amber" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Active workspaces" value={overview?.active_workspaces} detail={`${overview?.expiring_soon_workspaces || 0} expire within 5 days`} icon={<PaymentsRoundedIcon />} tone="green" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Suspended / rejected" value={(overview?.suspended_workspaces || 0) + (overview?.rejected_workspaces || 0)} detail={`${overview?.suspended_workspaces || 0} suspended`} icon={<WarningAmberRoundedIcon />} tone="red" />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard label="Connectors online" value={overview?.online_connectors} detail={`${overview?.offline_connectors || 0} offline`} icon={<CableRoundedIcon />} tone="blue" />
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Platform operations sections">
            <Tab label={`Workspaces (${workspaces.total || overview?.total_workspaces || 0})`} />
            <Tab label={`Connectors (${connectors.total || overview?.total_connectors || 0})`} />
            <Tab label={`Stores (${stores.total || overview?.total_stores || 0})`} />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, borderTop: "1px solid #e2e8f0" }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} mb={3}>
            <TextField
              fullWidth
              size="small"
              placeholder={tab === 0 ? "Search business, owner, phone, or workspace ID" : "Search workspace, store, connector, device, or owner"}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
            />

            {tab === 0 && (
              <>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Workspace status</InputLabel>
                  <Select value={workspaceStatus} label="Workspace status" onChange={(event) => setWorkspaceStatus(event.target.value)}>
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="pending_approval">Pending approval</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="access_expired">Access expired</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Billing</InputLabel>
                  <Select value={billingStatus} label="Billing" onChange={(event) => setBillingStatus(event.target.value)}>
                    <MenuItem value="">All billing</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="trial">Trial</MenuItem>
                    <MenuItem value="waived">Waived</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Onboarding</InputLabel>
                  <Select value={onboardingMethod} label="Onboarding" onChange={(event) => setOnboardingMethod(event.target.value)}>
                    <MenuItem value="">All methods</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                    <MenuItem value="excel">Excel</MenuItem>
                    <MenuItem value="pos_connector">POS connector</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {tab === 1 && (
              <>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Connector status</InputLabel>
                  <Select value={connectorStatus} label="Connector status" onChange={(event) => setConnectorStatus(event.target.value)}>
                    <MenuItem value="">All statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                    <MenuItem value="revoked">Revoked</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Health</InputLabel>
                  <Select value={health} label="Health" onChange={(event) => setHealth(event.target.value)}>
                    <MenuItem value="">All health</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="offline">Offline</MenuItem>
                    <MenuItem value="disabled">Disabled</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {tab === 2 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Store status</InputLabel>
                <Select value={storeStatus} label="Store status" onChange={(event) => setStoreStatus(event.target.value)}>
                  <MenuItem value="">All statuses</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="disabled">Disabled</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>

          {loading ? (
            <LoadingPanel label="Loading platform operations…" />
          ) : tab === 0 ? (
            workspaces.items?.length ? (
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell>Business</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Access</TableCell>
                      <TableCell>Onboarding</TableCell>
                      <TableCell>Usage</TableCell>
                      <TableCell>Created / note</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workspaces.items.map((workspace) => (
                      <TableRow key={workspace.workspace_id} hover>
                        <TableCell sx={{ minWidth: 210 }}>
                          <Typography fontWeight={900}>{workspace.workspace_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{workspace.business_type || "Business type not provided"}</Typography>
                          <Typography variant="caption" display="block" fontFamily="monospace" color="text.secondary">{workspace.workspace_id}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          <Typography fontWeight={750}>{workspace.owner_email || "—"}</Typography>
                          <Typography variant="caption" color="text.secondary">@{workspace.owner_username || "unknown"}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">{workspace.contact_phone || "No phone"}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 165 }}>
                          <Stack spacing={0.6} alignItems="flex-start">
                            <Chip size="small" label={titleCase(workspace.effective_status)} color={statusColor(workspace.effective_status)} />
                            <Chip size="small" label={titleCase(workspace.billing_status)} color={statusColor(workspace.billing_status)} variant="outlined" />
                            <Typography variant="caption" color="text.secondary">Expires: {formatDate(workspace.access_expires_at)}</Typography>
                            <Typography variant="caption" color={workspace.days_remaining <= 5 ? "warning.main" : "text.secondary"} fontWeight={workspace.days_remaining <= 5 ? 850 : 500}>
                              {workspace.effective_status === "active" ? `${workspace.days_remaining} day(s) remaining` : "Access locked"}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 145 }}>
                          <Chip size="small" label={titleCase(workspace.onboarding_method)} variant="outlined" />
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.6 }}>{workspace.plan_code}</Typography>
                          {workspace.payment_reference && <Typography variant="caption" display="block" color="text.secondary">Ref: {workspace.payment_reference}</Typography>}
                        </TableCell>
                        <TableCell sx={{ minWidth: 145 }}>
                          <Typography variant="body2">{workspace.store_count} store(s)</Typography>
                          <Typography variant="body2">{workspace.product_count.toLocaleString()} products</Typography>
                          <Typography variant="body2">{workspace.connector_count} connector(s)</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 210 }}>
                          <Typography variant="body2">{formatDate(workspace.created_at)}</Typography>
                          {workspace.status_reason && (
                            <Tooltip title={workspace.status_reason}>
                              <Typography variant="caption" color="text.secondary" display="block" noWrap maxWidth={220}>
                                {workspace.status_reason}
                              </Typography>
                            </Tooltip>
                          )}
                          {workspace.status_changed_by_email && <Typography variant="caption" color="text.secondary">By {workspace.status_changed_by_email}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 190 }}>
                          <WorkspaceActions
                            workspace={workspace}
                            canControl={canControl}
                            canDelete={canDelete}
                            onAction={(type, item) => setWorkspaceAction({ type, workspace: item })}
                            onHistory={setSubscriptionHistoryWorkspace}
                            onAllowance={setAllowanceWorkspace}
                            onDelete={setWorkspaceDelete}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyPanel icon="🏢" title="No workspaces match these filters" description="Change search, status, billing, or onboarding filters." />
            )
          ) : tab === 1 ? (
            connectors.items?.length ? (
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#f8fafc" }}>
                    <TableRow>
                      <TableCell>Connector</TableCell>
                      <TableCell>Workspace / store</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Device</TableCell>
                      <TableCell>Heartbeat</TableCell>
                      <TableCell>Latest sync</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {connectors.items.map((connector) => (
                      <TableRow key={connector.connector_id} hover>
                        <TableCell sx={{ minWidth: 195 }}>
                          <Typography fontWeight={900}>{connector.name}</Typography>
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">{connector.connector_id}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">Owner: {connector.owner_email || "—"}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 210 }}>
                          <Typography fontWeight={800}>{connector.workspace_name}</Typography>
                          <Chip size="small" label={titleCase(connector.workspace_status)} color={statusColor(connector.workspace_status)} variant="outlined" sx={{ my: 0.5 }} />
                          <Typography variant="body2">{connector.store_name}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 135 }}>
                          <Stack spacing={0.6} alignItems="flex-start">
                            <Chip size="small" label={titleCase(connector.status)} color={statusColor(connector.status)} variant="outlined" />
                            <Chip size="small" label={titleCase(connector.health)} color={statusColor(connector.health)} />
                            <Typography variant="caption">{connector.pending_batches || 0} pending</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 150 }}>
                          <Typography variant="body2">{connector.device_name || "Not activated"}</Typography>
                          <Typography variant="caption" color="text.secondary">v{connector.version || "—"}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">{connector.last_ip_address || "No IP"}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 155 }}>
                          <Typography variant="body2">{formatDate(connector.last_seen_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">Reported: {titleCase(connector.reported_status || "unknown")}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 175 }}>
                          <Chip size="small" label={titleCase(connector.latest_sync_status || "no sync")} color={statusColor(connector.latest_sync_status)} variant="outlined" />
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.6 }}>{formatDate(connector.latest_sync_started_at)}</Typography>
                          {connector.latest_sync_error && <Tooltip title={connector.latest_sync_error}><Typography variant="caption" color="error" display="block" noWrap maxWidth={200}>{connector.latest_sync_error}</Typography></Tooltip>}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 210 }}>
                          {canControl ? (
                            <Stack direction="row" spacing={0.7} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                              {connector.status === "active" && <Button size="small" variant="outlined" onClick={() => setConnectorAction({ type: "disable", connector })}>Disable</Button>}
                              {connector.status === "disabled" && <Button size="small" variant="contained" onClick={() => setConnectorAction({ type: "enable", connector })}>Enable</Button>}
                              {connector.status !== "revoked" && <Button size="small" color="error" variant="outlined" onClick={() => setConnectorAction({ type: "revoke", connector })}>Revoke</Button>}
                            </Stack>
                          ) : <Chip size="small" label="Read only" variant="outlined" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <EmptyPanel icon="🔌" title="No connectors match these filters" description="Change search or status filters." />
            )
          ) : stores.items?.length ? (
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Workspace</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Connector</TableCell>
                    <TableCell>Last heartbeat</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stores.items.map((store) => (
                    <TableRow key={store.store_id} hover>
                      <TableCell>
                        <Typography fontWeight={850}>{store.store_name}</Typography>
                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">{store.store_id}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={750}>{store.workspace_name}</Typography>
                        <Chip size="small" label={titleCase(store.workspace_status)} color={statusColor(store.workspace_status)} variant="outlined" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell><Chip size="small" label={titleCase(store.status)} color={statusColor(store.status)} variant="outlined" /></TableCell>
                      <TableCell>
                        {store.connector_id ? (
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Chip size="small" label={titleCase(store.connector_health)} color={statusColor(store.connector_health)} />
                            <Typography variant="caption" fontFamily="monospace" color="text.secondary">{store.connector_id}</Typography>
                            <Typography variant="caption" color="text.secondary">v{store.connector_version || "—"}</Typography>
                          </Stack>
                        ) : <Chip size="small" label="No connector" variant="outlined" />}
                      </TableCell>
                      <TableCell>{formatDate(store.connector_last_seen_at)}</TableCell>
                      <TableCell>{formatDate(store.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyPanel icon="🏬" title="No stores match these filters" description="Change search or status filters." />
          )}

          {!loading && pageCount > 1 && (
            <Stack alignItems="center" mt={3}>
              <Pagination count={pageCount} page={currentPage} onChange={(_, value) => setCurrentPage(value)} color="primary" shape="rounded" />
            </Stack>
          )}
        </Box>
      </Card>

      <WorkspaceActionDialog action={workspaceAction} onClose={() => !submitting && setWorkspaceAction(null)} onSubmit={submitWorkspaceAction} submitting={submitting} />
      <WorkspaceDeleteDialog
        workspace={workspaceDelete}
        onClose={() => !submitting && setWorkspaceDelete(null)}
        onSubmit={submitWorkspaceDelete}
        submitting={submitting}
      />
      {subscriptionHistoryWorkspace && (
        <SubscriptionHistoryDialog
          workspace={subscriptionHistoryWorkspace}
          onClose={() => setSubscriptionHistoryWorkspace(null)}
        />
      )}
      {allowanceWorkspace && (
        <AIAllowanceDialog
          workspace={allowanceWorkspace}
          canControl={canControl}
          onClose={() => setAllowanceWorkspace(null)}
          onSaved={() => {
            setToast("AI allowance updated.");
            setAllowanceWorkspace(null);
            load({ silent: true });
          }}
        />
      )}
      <ConnectorActionDialog action={connectorAction} onClose={() => !submitting && setConnectorAction(null)} onSubmit={submitConnectorAction} submitting={submitting} />

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast("")} message={toast} />
    </MainLayout>
  );
}
